import { useMemo, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { money } from "../api";
import { RecipeModal } from "./Recipes";
import { api, errorMessage } from "../api";

export const MENU_CATEGORIES = ["Starters", "Mains", "Desserts", "Beverages", "Breads", "Sides", "Combos"];
const title = (c) => (c || "Other").toLowerCase().replace(/\b\w/g, (x) => x.toUpperCase());

// Groups dishes by category regardless of letter case; standard categories first, custom ones after.
export function groupByCategory(recipes) {
  const g = new Map(MENU_CATEGORIES.map((c) => [c.toUpperCase(), { label: c, items: [] }]));
  for (const r of recipes) {
    const k = (r.category || "Other").trim().toUpperCase();
    if (!g.has(k)) g.set(k, { label: title(k), items: [] });
    g.get(k).items.push(r);
  }
  return [...g.values()];
}

export default function MenuView({ recipes, inventory, branch, onChanged }) {
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  window.__fd_branch = branch;
  const grouped = useMemo(() => groupByCategory(recipes), [recipes]);

  const doDelete = async () => {
    setBusy(true);
    try { await api.delete(`/recipes/${confirm.id}`); setConfirm(null); onChanged?.(); }
    catch (e) { alert(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const toggle = async (r) => {
    try { await api.put(`/recipes/${r.id}/availability`, { available: r.available === false }); onChanged?.(); } catch (e) { alert(errorMessage(e)); }
  };

  return (
    <>
      <div className="recipes-head">
        <div><span className="overline">MENU &amp; PRICING</span><h2 style={{fontSize:20,margin:"6px 0 2px"}}>Your customer-facing menu</h2><p style={{margin:0,color:"var(--muted)",fontSize:13}}>Every dish from Recipes &amp; Costing appears here. Prices flow to POS and GST invoices automatically.</p></div>
        <button className="primary-button" onClick={() => setModal({ open: true, initial: null })} data-testid="add-menu-item"><Plus size={16}/> Add menu item</button>
      </div>

      <section className="menu-board" data-testid="menu-board">
        {grouped.map(({ label, items }) => (
          <div className="menu-section" key={label} data-testid={`menu-section-${label}`}>
            <span className="overline">{label}</span>
            {items.length ? items.map(r => (
              <div className="menu-item" key={r.id} data-testid={`menu-row-${r.id}`} style={r.available === false ? { opacity: 0.55 } : undefined}>
                <div className="menu-item-info">
                  <b>{r.name}</b>
                  <span>{r.ingredients_count || (r.ingredients || []).length} ingredients · GST {r.gst_rate ?? 5}% · HSN {r.hsn_sac || "2106"}</span>
                </div>
                <strong data-testid={`menu-price-${r.id}`}>{money(r.selling_price)}</strong>
                <div className="menu-item-actions">
                  <button className={`pill ${r.available === false ? "mod-cancel" : "success"}`} style={{ border: 0, cursor: "pointer" }} title="Tap to change" onClick={() => toggle(r)} data-testid={`menu-available-${r.id}`}>{r.available === false ? "Unavailable" : "Available"}</button>
                  <button className="icon-button" title="Edit" onClick={() => setModal({ open: true, initial: r })} data-testid={`edit-menu-${r.id}`}><Pencil size={14}/></button>
                  <button className="icon-button" title="Delete" onClick={() => setConfirm(r)} data-testid={`delete-menu-${r.id}`}><Trash2 size={14}/></button>
                </div>
              </div>
            )) : <div className="empty small">No items yet</div>}
          </div>
        ))}
      </section>

      <RecipeModal
        open={!!modal}
        initial={modal?.initial}
        inventory={inventory}
        onClose={() => setModal(null)}
        onSaved={() => onChanged?.()}
      />

      {confirm && (
        <div className="modal-backdrop">
          <div className="modal" data-testid="menu-delete-modal">
            <div className="modal-head"><div><span className="overline">CONFIRM</span><h2>Remove this menu item?</h2></div></div>
            <p style={{color:"var(--muted)",fontSize:13}}>“{confirm.name}” will no longer appear in POS or on new bills.</p>
            <div className="setup-actions" style={{marginTop:14}}>
              <button className="secondary-button" onClick={() => setConfirm(null)}>Cancel</button>
              <span style={{flex:1}}/>
              <button className="primary-button" style={{background:"#8a2a2a"}} disabled={busy} onClick={doDelete} data-testid="confirm-delete-menu">Delete item</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
